// @ts-nocheck FIXME
import {handleError, FetchError} from "./api.js";
import {coreApiCall} from "./api-core.js";
import {apiConfig as config} from "./index.js";
import type {accessTokenUuid, accountUuid, account, project, projectUuid} from "../../model/index.js";

export default class ApiBackend {
	async callApi(options: any): Promise<any> {
		return await coreApiCall(options);
	}

	async getAppConfig() {
		return await coreApiCall({
			url:                "app-config",
			expectedStatusCode: 200,
			expectedJson:       true,
			statusCodeHandlers: {
				200: async response => {
					if (response.items === undefined)
						throw new FetchError("data", response);

					return response.items;
				}
			},
			errorMessage: "Error getting application configuration."
		});
	}

	/**
	 * Send a request to create a new annotation.
	 *
	 * @param {Object} annotation Annotation data object
	 * @param {string} project_uuid
	 * @param {string|undefined} topic_uuid
	 * @param {boolean} prepend
	 * @return {Promise<Object>} Annotation position, rendered_citation, etc.
	 */
	async createAnnotation(annotation, project_uuid, topic_uuid, prepend) {
		const annotationData = {prepend, project_uuid, topic_uuid, ...annotation};

		// The API can't handle these fields as part of the input
		delete annotationData.account_uuid;
		delete annotationData.created_by;

		return await coreApiCall({
			url:    "annotation",
			method: "POST",
			body:   new URLSearchParams({
				data: JSON.stringify(annotationData)
			}),
			expectedStatusCode: 201,
			expectedJson:       true,
			statusCodeHandlers: {
				201: async response => {
					if (response.position === undefined)
						throw new FetchError("data", response);

					// response has the following:
					//	position
					//	rendered_citation
					return response;
				}
			},
			errorMessage: "Error while saving annotation."
		});
	}

	/**
	 * Send a request to create a new project.
	 *
	 * @param {Object} project Project data object
	 */
	async createProject(project) {
		const projectData = {
			uuid:        project.uuid,
			name:        project.name,
			description: project.description,
			created_dt:  project.created_dt,
			settings:    project.settings
		};

		await coreApiCall({
			url:    "project",
			method: "POST",
			body:   new URLSearchParams({
				data: JSON.stringify(projectData)
			}),
			expectedStatusCode: 201,
			errorMessage:       "Error while saving project."
		});
	}

	/**
	 * Send a request to create a new topic.
	 *
	 * @param {Object} topic Topic data object
	 */
	async createTopic(topic) {
		const topicData = {
			project_uuid: topic.project_uuid,
			uuid:         topic.uuid,
			name:         topic.name,
			label:        topic.label,
			color:        topic.color,
			position:     topic.position
		};

		await coreApiCall({
			url:    "topic",
			method: "POST",
			body:   new URLSearchParams({
				data: JSON.stringify(topicData)
			}),
			expectedStatusCode: 201,
			errorMessage:       "Error while creating topic."
		});
	}

	/**
	 * Send a request to update an existing topic.
	 *
	 * @param {string} topicUUID Topic uuid
	 * @param {Object} data      Topic data object
	 */
	async updateTopic(topicUUID, data) {
		await coreApiCall({
			url:    `topic/${topicUUID}`,
			method: "PUT",
			body:   new URLSearchParams({
				data: JSON.stringify(data)
			}),
			expectedStatusCode: 201,
			errorMessage:       "Error while updating topic."
		});
	}

	/**
	 * Send a request to update a project's settings.
	 *
	 * @param {string} projectUuid Project uuid
	 * @param {Object} data        Project settings object
	 */
	async updateProjectSettings(projectUuid, data) {
		await coreApiCall({
			url:    `project/${projectUuid}`,
			method: "PATCH",
			body:   new URLSearchParams({
				data: JSON.stringify({
					settings: data
				})
			}),
			expectedStatusCode: 204,
			errorMessage:       "Error while updating project settings."
		});
	}

	/**
	 * Retrieve annotations.
	 *
	 * @param {string} projectUuid
	 * @return {Promise<Object[]>}
	 */
	async getMergedOutline(projectUuid) {
		const outline = await this.getOutline(projectUuid);
		const annotations = await this.getAnnotations(projectUuid);
		return this.mergeAnnotationsIntoOutline(outline, annotations);
	}

	async getAnnotations(projectUuid) {
		return await coreApiCall({
			url:                   "annotation",
			queryStringParameters: {
				project_uuid: projectUuid
			},
			expectedStatusCode: 200,
			expectedJson:       true,
			statusCodeHandlers: {
				200: async response => {
					if (response.annotations === undefined)
						throw new FetchError("data", response);

					return response.annotations;
				}
			},
			errorMessage: "Error getting annotations from server."
		});
	}

	getAnnotationListFromOutline(outline) {
		// TODO: switch this to use the spread operator
		// eslint-disable-next-line prefer-spread
		return [].concat.apply([], outline.map(function (outlineItem) {
			return outlineItem.annotations.map(function (annotation) {
				return annotation.annotation_uuid;
			});
		}));
	}

	mergeAnnotationsIntoOutline(outline, annotations) {
		return outline
			.map(function (outlineElement) {
				outlineElement.annotations = outlineElement.annotations
					.map(annotation => this.filterAnnotationByUUID(annotation, annotations))
					.filter(annotation => typeof annotation !== "undefined");

				return outlineElement;
			})
			.filter(outlineElement => outlineElement.annotations.length > 0);
	}

	filterAnnotationByUUID(rawAnnotation, annotations) {
		const annotation = annotations.filter(function (_annotation) {
			return rawAnnotation.annotation_uuid === _annotation.uuid;
		})[0];

		if (annotation)
			annotation.position = rawAnnotation.position;

		return annotation;
	}

	/**
	 * Send a request to delete an annotation.
	 *
	 * @param {string} annotationUuid
	 */
	async deleteAnnotation(annotationUuid) {
		await coreApiCall({
			url:                `annotation/${annotationUuid}`,
			method:             "DELETE",
			expectedStatusCode: 204,
			errorMessage:       "Error deleting annotation."
		});

		// @TODO: remove topic from DOM on error?
	}

	/**
	 * Send a request to update an existing annotation.
	 *
	 * @param {string} annotationUuid
	 * @param {Object} data           Annotation data object with changed data
	 */
	async updateAnnotation(annotationUuid, data) {
		await coreApiCall({
			url:    `annotation/${annotationUuid}`,
			method: "PUT",
			body:   new URLSearchParams({
				data: JSON.stringify(data)
			}),
			expectedStatusCode: 201,
			errorMessage:       "Error updating annotation."
		});
	}

	/**
	 * Get the rendered citation.
	 *
	 * @param {Object} data    Annotation data object with changed data
	 * @returns {Promise<string>}
	 */
	async getRenderedCitation(data) {
		return await coreApiCall({
			url:                "citations",
			method:             "POST",
			body:               JSON.stringify(data),
			expectedStatusCode: 200,
			expectedJson:       true,
			statusCodeHandlers: {
				200: async response => {
					if (response.rendered_citation === undefined)
						throw new FetchError("data", response);

					return response.rendered_citation;
				}
			},
			errorMessage: "Error getting rendered citation."
		});
	}

	async getAccount(uuid) {
		return await coreApiCall({
			url:                "account/" + encodeURIComponent(uuid),
			expectedStatusCode: 200,
			expectedJson:       true,
			statusCodeHandlers: {
				200: async response => {
					if (response.account === undefined)
						throw new FetchError("data", response);

					return response.account;
				}
			},
			errorMessage: "Error getting account."
		});
	}

	async updateAccount(uuid: accountUuid, data: any): Promise<{re_verification_email_sent: boolean}> {
		const body = [];
		Object.keys(data).forEach(field => body.push({
			op:    "replace",
			path:  "/" + field,
			value: data[field]
		}));

		return await coreApiCall({
			url:                "account/" + encodeURIComponent(uuid),
			method:             "PATCH",
			body,
			isJsonBody:         true,
			expectedJson:       true,
			expectedStatusCode: 200,
			errorMessage:       "Error updating account."
		});
	}

	/**
	 * This is used when we ask the web app for the user's account info,
	 * so we can log them in to the extension without asking for their creds again.
	 *
	 * @param {Object} data
	 * @param {string} data.account_uuid
	 * @param {string} data.access_token
	 */
	async login(data) {
		return await coreApiCall({
			url:                "account/" + encodeURIComponent(data.account_uuid),
			headers:            {"X-ACCESS-TOKEN": data.access_token},
			expectedStatusCode: 200,
			expectedJson:       true,
			statusCodeHandlers: {
				200: async response => {
					if (response.account === undefined)
						throw new FetchError("data", response);

					response.access_token = data.access_token;
					return response;
				}
			},
			errorMessage: "Error logging in."
		});
	}

	/**
	 * Retrieve a single project.
	 *
	 * @return {Promise<Object>}
	 */
	async getProject(uuid) {
		return await coreApiCall({
			url:                "project/" + encodeURIComponent(uuid),
			expectedStatusCode: 200,
			expectedJson:       true,
			statusCodeHandlers: {
				200: async response => {
					if (response.project === undefined)
						throw new FetchError("data", response);

					return response.project;
				}
			},
			errorMessage: "Error getting project."
		});
	}

	/**
	 * Retrieve projects for the authenticated user.
	 *
	 * @return {Promise<Object[]>}
	 */
	async getProjects() {
		return await coreApiCall({
			url:                "project",
			expectedStatusCode: 200,
			expectedJson:       true,
			statusCodeHandlers: {
				200: async response => {
					if (response.projects === undefined)
						throw new FetchError("data", response);

					return response.projects;
				}
			},
			errorMessage: "Error getting projects."
		});
	}

	/**
	 * Retrieve topics for the selected project and authenticated user.
	 *
	 * @param {string} projectUuid
	 * @return {Promise<Object[]>}
	 */
	async getTopics(projectUuid) {
		return await coreApiCall({
			url:                   "topic",
			queryStringParameters: {
				project_uuid: projectUuid
			},
			expectedStatusCode: 200,
			expectedJson:       true,
			statusCodeHandlers: {
				200: async response => {
					if (response.topics === undefined)
						throw new FetchError("data", response);

					return response.topics;
				}
			},
			errorMessage: "Error getting topics."
		});
	}

	/**
	 * Retrieve outline for the selected project and authenticated user.
	 *
	 * @param {string} projectUuid
	 * @return {Promise<Object>}
	 */
	async getOutline(projectUuid) {
		return await coreApiCall({
			url:                   "outline",
			queryStringParameters: {
				project_uuid: projectUuid
			},
			expectedStatusCode: 200,
			expectedJson:       true,
			statusCodeHandlers: {
				200: async response => {
					if (response.outline === undefined)
						throw new FetchError("data", response);

					return response.outline;
				},
				404: async () => {
					// Sometimes when creating a new project, the API
					// hasn't finished processing the create request
					// before the UI requests the outline.
					// We'll return an empty outline so we don't show
					// the user an error. This is also okay in cases
					// where there is some bizarre error leading to an
					// outline actually not existing, since it should
					// just be empty anyway.
					console.warn("Assuming missing outline is just not created yet");
					return [];
				}
			},
			errorMessage: "Error getting outline."
		});
	}

	/**
	 * Send a request to change an annotation topic.
	 *
	 * @param {string} annotationUuid
	 * @param {string} newTopicUuid
	 * @param {string} projectUuid
	 * @return {Promise<number>} Annotation's position
	 */
	async changeAnnotationTopic(annotationUuid, newTopicUuid, projectUuid) {
		return await coreApiCall({
			url:    "outline",
			method: "PATCH",
			body:   new URLSearchParams({
				data: JSON.stringify({
					op:              "change-annotation-topic",
					annotation_uuid: annotationUuid,
					new_topic_uuid:  newTopicUuid,
					project_uuid:    projectUuid
				})
			}),
			expectedStatusCode: 201,
			expectedJson:       true,
			statusCodeHandlers: {
				201: async response => {
					if (response.position === undefined)
						throw new FetchError("data", response);

					return response.position;
				}
			},
			errorMessage: "Error changing annotation topic."
		});
	}

	/**
	 * Send a request to copy an annotation to another project.
	 *
	 * @param {string} annotationUuid
	 * @param {string} newTopicUuid
	 * @param {string} projectUuid
	 * @return {Promise<number>} Annotation's position
	 */
	async copyAnnotationToProject(annotationUuid, newProjectUuid, newTopicUuid) {
		return await coreApiCall({
			url:    "outline",
			method: "PATCH",
			body:   new URLSearchParams({
				data: JSON.stringify({
					op:               "copy-annotation",
					new_project_uuid: newProjectUuid,
					new_topic_uuid:   newTopicUuid,
					annotation_uuid:  annotationUuid
				})
			}),
			expectedStatusCode: 201,
			expectedJson:       true,
			errorMessage:       "Error copying annotation."
		});
	}

	/**
	 * Send user submitted feedback to the server.
	 *
	 * @param {Object} data Feedback data
	 */
	async sendFeedback(data) {
		// Exclude sendDebug from data
		const {sendDebug, ...dataToSend} = data;

		dataToSend.type = "extension-feedback";

		if (sendDebug) {
			dataToSend.debug = {
				logs: {
					background: window.logsCollector.getLogs(),
					foreground: []
				}
			};
		}

		await coreApiCall({
			url:                "feedback",
			method:             "POST",
			body:               dataToSend,
			isJsonBody:         true,
			expectedStatusCode: 201,
			errorMessage:       "Error sending feedback."
		});

		// Clean up the logs
		if (sendDebug)
			window.logsCollector.clearLogs();
	}

	/**
	 * Sends a PDF document for conversion to "searchable PDF".
	 *
	 * @param {string} url Original URL for the document
	 * @param {int} pages Number of pages detected
	 * @param {string} language E.g. "english"
	 * @param {string} objectURL PDF data
	 * @return {Promise<Object>}
	 */
	async convertOcr(url, pages, language, objectURL) {
		let pdfData;

		try {
			const r = await fetch(objectURL);
			pdfData = await r.arrayBuffer();
			URL.revokeObjectURL(objectURL);
		} catch (error) {
			await handleError("Error preparing PDF for OCR conversion.", error);
		}

		return await coreApiCall({
			url:                   "ocr",
			queryStringParameters: {
				url,
				pages: parseInt(pages, 10),
				language
			},
			method:  "POST",
			body:    pdfData,
			headers: {
				"Content-Type": "application/octet-stream"
			},
			expectedStatusCode: 201,
			expectedJson:       true,
			errorMessage:       "Error sending PDF for OCR conversion."
		});
	}

	/**
	 * Checks the status of a PDF OCR conversion.
	 *
	 * @param {string} url Original URL for the document
	 * @return {Promise<Object>}
	 */
	async checkOcrStatus(url) {
		return await coreApiCall({
			url:                   "ocr",
			queryStringParameters: {
				url
			},
			expectedStatusCode: 200,
			expectedJson:       true,
			errorMessage:       "Error checking PDF OCR conversion."
		});
	}

	async uploadAnnotationImage(imgData, imgType, annotationUuid) {
		// Get pn-files-api url
		const url = new URL(config.urls.pn_files_api + "annotation/" + encodeURIComponent(annotationUuid));

		return await coreApiCall({
			url,
			method:  "POST",
			headers: {
				"Content-Type": imgType
			},
			body:               imgData,
			expectedStatusCode: 204,
			statusCodeHandlers: {
				400: async response => {
					return response;
				}
			},
			errorMessage: "Error saving image."
		});
	}

	async uploadAnnotationImageByUrl(imgUrl, annotationUuid) {
		// Get pn-files-api url
		const url = new URL(config.urls.pn_files_api + "annotation/" + encodeURIComponent(annotationUuid) + "/url");

		return await coreApiCall({
			url,
			method:             "POST",
			body:               {url: imgUrl},
			isJsonBody:         true,
			expectedStatusCode: 204,
			statusCodeHandlers: {
				400: async response => {
					return response;
				}
			},
			errorMessage: "Error saving image."
		});
	}

	async updatePreferences(data) {
		return await coreApiCall({
			url:                "preferences",
			method:             "POST",
			body:               data,
			isJsonBody:         true,
			expectedStatusCode: 204,
			errorMessage:       "Error updating preferences."
		});
	}

	/**
	 * Accept the time-tracking terms for the given project.
	 *
	 * @param {string} projectUuid
	 * @param {bool} reject
	 */
	async acceptTimeTrackingTerms(projectUuid, reject = false) {
		return await coreApiCall({
			url:                `project/${projectUuid}/time-tracking`,
			method:             reject ? "DELETE" : "POST",
			expectedStatusCode: 204,
			errorMessage:       reject ? "Error rejecting terms." : "Error accepting terms."
		});
	}

	/**
	 * Accept the time-tracking terms for the given project.
	 *
	 * @param {Object[]} data
	 * @param {string} data[].project_uuid
	 * @param {number} data[].duration_ms
	 * @param {Date} data[].end_dt
	 * @param {string} data[].page_url
	 * @param {string} [data[].clean_url]
	 * @param {Object.<string, number>} [data[].activity]
	 */
	async logTimeOnPage(data) {
		return await coreApiCall({
			url:                "time/page",
			method:             "POST",
			keepalive:          true,
			body:               data,
			isJsonBody:         true,
			expectedStatusCode: 201,
			errorMessage:       "Error logging time on page."
		});
	}

	/**
	 * Log arbitrary event data. Note that pn-api enforces which event types can be sent using this endpoint.
	 *
	 * @param {Object} data
	 * @param {string} data.type
	 * @param {number} data.version
	 * @param {Object} data.data
	 * @param {string} [data.dt]
	 * @param {string} [data.owner_uuid]
	 */
	async event(data) {
		return await coreApiCall({
			url:                "event",
			method:             "POST",
			keepalive:          true,
			body:               data,
			isJsonBody:         true,
			expectedStatusCode: 201,
			errorMessage:       "Error sending event data."
		});
	}

	/**
	 * Run an library search.
	 *
	 * @param {string} query
	 * @param {string} source E.g. "google" (as in "integration with Google search results page")
	 * @return {Promise<{results: {content: string, description: string}[]}>}
	 */
	async librarySearch(query, source) {
		return await coreApiCall({
			url:                   "library-search/" + encodeURIComponent(source),
			queryStringParameters: {q: query},
			expectedStatusCode:    200,
			expectedJson:          true,
			errorMessage:          "Unknown search error."
		});
	}

	async getNotifications(limit, include_dismissed) {
		return await coreApiCall({
			url:                   "notification",
			queryStringParameters: {
				limit,
				include_dismissed
			},
			expectedStatusCode: 200,
			expectedJson:       true,
			statusCodeHandlers: {
				200: async response => {
					if (response.notifications === undefined)
						throw new FetchError("data", response);

					return response.notifications;
				}
			},
			errorMessage: "Error getting notifications."
		});
	};

	async createTempAccount(clientToken: string, integration: string): Promise<{access_token: accessTokenUuid; account: account; project: project}> {
		if (!(/^[a-z]+$/.exec(integration)))
			throw new Error("Invalid integration: " + integration);

		return await coreApiCall({
			url:                   `integration/${integration}/account`,
			queryStringParameters: {client_token: clientToken},
			method:                "POST",
			expectedStatusCode:    201,
			expectedJson:          true,
			statusCodeHandlers:    {
				201: async response => {
					if (response.access_token === undefined)
						throw new FetchError("data", response);

					// response has the following:
					//	access_token
					//	account
					//	project
					return response;
				}
			},
			errorMessage: "Error while saving annotation."
		});
	}

	async integrationLogin(integration: string, uuid: accountUuid, email: string, password: string, projectUuid: projectUuid | null = null): Promise<{account_uuid: accountUuid; access_token: accessTokenUuid; project_uuid?: projectUuid}> {
		const body = {email, password};
		if (projectUuid)
			body.project_uuid = projectUuid;

		return await coreApiCall({
			url:                "integration/" + integration + "/account/" + uuid + "/login",
			method:             "POST",
			body,
			isJsonBody:         true,
			expectedJson:       true,
			expectedStatusCode: 200,
			errorMessage:       "Please check your email and password."
		});
	}

	async upgradeTempAccount(integration: string, uuid: accountUuid, name: string, email: string, password: string, terms: boolean): Promise<{account: account}> {
		return await coreApiCall({
			url:                "integration/" + integration + "/account/" + uuid + "/upgrade",
			method:             "POST",
			body:               {name, email, password, terms},
			isJsonBody:         true,
			expectedJson:       true,
			expectedStatusCode: 200,
			errorMessage:       "Error upgrading integration account.",
			errorCodeMsgMap:    {
				4013: () => {
					throw new Error("The given email address belongs to an existing PowerNotes account.");
				},
				4090: () => {
					throw new Error("Account already upgraded.");
				},
				4091: async (response, body) => {
					let message = "Error upgrading integration account.";

					// Recover error message from pn-auth (e.g. "password is not secure")
					if (body?.error?.details?.length === 1 && typeof body.error.details[0] === "string")
						message = body.error.details[0];

					throw new Error(message);
				}
			}
		});
	}

	async forgotPassword(email: string): Promise<void> {
		const encodedEmail = encodeURIComponent(email);

		return await coreApiCall({
			url:                "account/" + encodedEmail + "/reset-password",
			method:             "PUT",
			expectedStatusCode: 204,
			errorMessage:       "Error resetting password."
		});
	}
}
